import type { Dispatch, KeyboardEvent, SetStateAction } from 'react';
import { forwardRef, useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { focusVisibleMixin } from '@elfsight/widget-ui';
import useMergedRef from '@react-hook/merged-ref';
import { useSettings } from '@wu/widget-core';
import type { MenuItem } from '../../../../../../types';
import { Icon } from '../../../../common';

interface TabsControlItemProps {
  data: MenuItem;
  widths: number[];
  setWidths: Dispatch<SetStateAction<number[]>>;
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent) => void;
  className?: string;
  selectedMenu?: MenuItem;
  showIcon?: boolean;
}

export const TabsControlItem = forwardRef<HTMLDivElement, TabsControlItemProps>(
  (
    {
      data,
      className = '',
      selectedMenu,
      showIcon,
      widths,
      setWidths,
      onClick,
      onKeyDown
    },
    forwardedRef
  ) => {
    const {
      showMenuIcon,
      menuTabsFontSize: { fontSize: tabsFontSize },
      colorScheme,
      accentColor,
      textColor
    } = useSettings();

    const localRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergedRef<HTMLDivElement>(localRef, forwardedRef);

    const { id: selectedMenuId } = selectedMenu || {};
    const { id: itemId, name, icon } = data;

    useLayoutEffect(() => {
      if (localRef.current) {
        const {
          current: { clientWidth }
        } = localRef;

        const result = widths;
        // @ts-expect-error wtf is this idk
        result[`id_${data.id}`] = clientWidth;

        setWidths([...result]);
      }
    }, [localRef]);

    const isSelected = selectedMenuId === itemId;

    const iconSize = tabsFontSize * 2.4;
    const iconMarginBottom = tabsFontSize * 0.75;
    const iconColor =
      isSelected && accentColor
        ? accentColor
        : {
            light: '#111',
            dark: '#fff',
            custom: textColor
          }[colorScheme];

    return (
      <TabsControlItemComponent
        ref={mergedRef}
        id={itemId}
        role="tab"
        aria-selected={isSelected}
        tabIndex={isSelected ? 0 : -1}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className={className}
        _selected={isSelected}
        _showIcon={showIcon && showMenuIcon}
        _widths={widths}
      >
        {showIcon && icon && (
          <Icon
            icon={icon}
            size={iconSize}
            fill={iconColor}
            style={{ marginBottom: `${iconMarginBottom}px` }}
            alt=""
          />
        )}

        <Name _selected={isSelected}>{name}</Name>
      </TabsControlItemComponent>
    );
  }
);

const Name = styled.div<{ _selected?: boolean }>`
  line-height: 1.5;
  font-size: ${({
    theme: {
      menuTabsFontSize: { fontSize }
    }
  }) => fontSize}px;
  font-weight: 700;
  position: relative;
  text-transform: ${({
    theme: {
      menuTitleFontStyle: { textTransform }
    }
  }) => textTransform};

  &::after {
    content: ' ';
    position: absolute;
    bottom: 0;
    height: 2px;
    display: block;
    transition: 0.1s ease opacity;
    opacity: ${({ _selected }) => (_selected ? 1 : 0)};
    width: 100%;

    ${({ _selected, theme: { colorScheme, textColor, accentColor } }) => css`
      ${!_selected &&
      css`
        ${colorScheme === 'light' &&
        css`
          background: #111;
        `}
        ${colorScheme === 'dark' &&
        css`
          background: #fff;
        `}
        ${colorScheme === 'custom' &&
        css`
          background: ${textColor};
        `}
      `}

      ${_selected &&
      accentColor &&
      css`
        background: ${accentColor};
      `}

      ${_selected &&
      !accentColor &&
      css`
        ${(colorScheme === 'light' &&
          css`
            background: #111;
          `) ||
        (colorScheme === 'dark' &&
          css`
            background: #fff;
          `) ||
        (colorScheme === 'custom' &&
          css`
            background: ${textColor};
          `)};
      `}
    `};
  }
`;

export const TabsControlItemComponent = styled.div<{
  _selected?: boolean;
  _showIcon?: boolean;
  _widths?: number[];
}>`
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: 0.3s ease all;
  padding-bottom: 2px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  margin: ${({ _showIcon }) => (_showIcon ? 0 : '12px')} 12px 12px;
  ${({ _widths, _showIcon }) =>
    _showIcon
      ? css`
          width: ${_widths?.some((item: number) => item) &&
          Math.max(..._widths)}px;
        `
      : ''};

  ${({ _selected, theme: { colorScheme, textColor, accentColor } }) => css`
    ${!_selected &&
    css`
      ${colorScheme === 'light' &&
      css`
        color: #111;
      `}
      ${colorScheme === 'dark' &&
      css`
        color: #fff;
      `}
      ${colorScheme === 'custom' &&
      css`
        color: ${textColor};
      `}
    `};

    ${_selected &&
    accentColor &&
    css`
      color: ${accentColor};
    `}

    ${_selected &&
    !accentColor &&
    css`
      ${(colorScheme === 'light' &&
        css`
          color: #111;
        `) ||
      (colorScheme === 'dark' &&
        css`
          color: #fff;
        `) ||
      (colorScheme === 'custom' &&
        css`
          color: ${textColor};
        `)};
    `}
  `};

  &:is(:hover, :focus-visible) ${Name}:after {
    opacity: 1;
  }

  ${focusVisibleMixin()}
`;
